// AddLegacyWarrantyModal — record a pre-CRM job so a warranty can hang off it.
//
// Why this exists (Aug 26, 2026): roofs we replaced in the Zoho era (2022-23)
// never converted into this system, so when one of those homeowners calls about
// a leak there is no deal to open a warranty claim against. This creates the
// bare contact + property record at closed_won, with the warranty fields filled
// in, and NOTHING else.
//
// ─────────────────────────────────────────────────────────────────────────────
// THE WHOLE POINT: inserting straight at closed_won is side-effect free.
// Verified against every trigger on public.deals before this shipped:
//
//   • trg_pm_apply_per_build_fee is AFTER **UPDATE** OF build_completed_date —
//     it does NOT fire on INSERT. So writing build_completed_date in the insert
//     never books Ramiro's $100 PM fee onto an open Risol crew row. (This is
//     the exact trap the Aug 26 backfill had to tiptoe around; here it is free.)
//     ⚠ Corollary: NEVER set build_completed_date on a later UPDATE from this
//     flow — do it in the INSERT or not at all.
//   • crew_log_stage_transition (on stage_history) credits only
//     to_stage='appointment_received' and leads→claims. closed_won returns
//     early → no leaderboard points, no crew_activity_log row.
//   • crew_log_deal_change DOES fire on INSERT, but every branch is gated on a
//     field we deliberately leave null: install_agreement_signed,
//     ho_preferred_build_date, commissionable revenue, feedback_rating.
//     ⚠ Do not add any of those four to the payload below.
//   • recompute_final_invoice_amount is gated to the supps lifecycle stages;
//     closed_won is not one → no invoice derived, stays out of AR.
//   • commission_ready_date stays null → CommissionBoard's
//     .not("commission_ready_date","is",null) filter never sees it.
//   • Homeowner lifecycle texts gate on appointment_received / drone_scheduled
//     → no text goes to the homeowner.
//   • knock_pin_id stays null → no canvas pin advance, no canvasser ping.
//   • referral_mark_earned_on_build fires (build date is set) but matches zero
//     referrals rows for a brand-new deal → no-op.
//   • trg_deal_created (the Zapier folder/automation_events hook) is DISABLED
//     at the DB level, so nothing is queued for Zapier either.
//
// Proven, not assumed: the exact payload below was inserted inside a
// BEGIN/ROLLBACK envelope on production and asserted — 0 crew_activity_log
// rows, 0 notifications, 0 roof_crew_payments delta, 0 referrals touched,
// 0 automation_events, null invoice/balance/commission fields, exactly 1
// stage_history row, and a warranty claim that attaches and auto-assigns to
// Jennifer. Re-run that envelope if you ever widen the payload.
//
// What DOES happen, by design:
//   • one stage_history row (source='system_initial_stage', to_stage=closed_won)
//   • one activity_log 'note' row tagging the record as a legacy entry
//     ('note' is a real activity_type enum value — see the Aug 21 lesson about
//     invalid enum values making audit inserts vanish silently)
//
// ⚠ The write goes through the create_legacy_warranty_deal RPC, NOT a client
// insert. Two reasons: (1) roof_team_manager is in no deals INSERT policy, so
// Robert could never have created one from the client; (2) the "never set these
// fields" rule above is only a comment here — in the RPC the column list IS the
// contract, so no caller can smuggle a money column in. Widen the RPC, never
// route around it. The actor on the audit row is resolved from the JWT
// server-side, so it can't be spoofed from here.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { fmtSupabaseError } from "../../lib/fmtError";
import { findDealOwnershipHints } from "../../lib/dealOwnershipHints";

interface Props {
  onClose: () => void;
  /** Called after a successful insert so the tab can refresh its lists. */
  onCreated: (dealId: string) => void | Promise<void>;
}

type RosterLite = { id: string; full_name: string; role: string };

const WARRANTY_TYPES = [
  "Owens Corning Preferred Protection",
  "Owens Corning Platinum Protection",
  "Owens Corning System Protection",
  "Bardin workmanship warranty",
  "Manufacturer standard (shingle only)",
  "Other",
];

// Roles that make sense as the "who sold it" attribution on a legacy job.
const OWNER_ROLES = new Set([
  "restore_manager",
  "team_lead",
  "asm",
  "sales_director",
  "roof_team_manager",
  "roof_director",
  "director_of_operations",
  "admin",
]);

/**
 * Add whole years to a YYYY-MM-DD string using STRING math.
 * Never `new Date("2022-05-09")` on a date-only column — that parses as UTC
 * midnight and formats back one day earlier in ET (the May 9, 2026 off-by-one).
 * Feb 29 + N years clamps to Feb 28 when the target year isn't a leap year.
 */
function addYearsISO(iso: string, years: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return "";
  const y = Number(m[1]) + years;
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const leap = (yy: number) => (yy % 4 === 0 && yy % 100 !== 0) || yy % 400 === 0;
  const day = mo === 2 && d === 29 && !leap(y) ? 28 : d;
  return `${String(y).padStart(4, "0")}-${String(mo).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export default function AddLegacyWarrantyModal({ onClose, onCreated }: Props) {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    homeowner_name: "",
    homeowner_phone: "",
    homeowner_email: "",
    property_street: "",
    property_city: "",
    property_state: "GA",
    property_zip: "",
    build_completed_date: "",
    warranty_type: WARRANTY_TYPES[0],
    oc_warranty_number: "",
    warranty_registered_date: "",
    warranty_expiration_date: "",
    owner_id: "",
    notes: "",
  });
  const [roster, setRoster] = useState<RosterLite[]>([]);
  const [dupes, setDupes] = useState<Array<{ id: string; label: string; sub: string }> | null>(null);
  const [dupeChecked, setDupeChecked] = useState(false);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function set<K extends keyof typeof form>(k: K, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
    // Any identity edit invalidates a previous duplicate clearance.
    if (k === "homeowner_name" || k === "property_street" || k === "property_city" || k === "homeowner_phone") {
      setDupeChecked(false);
      setDupes(null);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("team_roster")
        .select("id, full_name, role")
        .eq("is_active", true)
        .order("full_name");
      if (!cancelled && data) setRoster((data as RosterLite[]).filter((r) => OWNER_ROLES.has(r.role)));
    })();
    return () => { cancelled = true; };
  }, []);

  const nameOk = form.homeowner_name.trim().length > 1;
  const streetOk = form.property_street.trim().length > 2;
  const cityOk = form.property_city.trim().length > 1;
  const canSubmit = nameOk && streetOk && cityOk && !saving;

  /**
   * Best-effort duplicate look. Two sources on purpose:
   *  - find_deal_ownership_hints (SECURITY DEFINER) sees LIVE deals owned by
   *    other reps that this viewer's RLS would otherwise hide.
   *  - a plain deals select catches terminal/closed rows the hints RPC skips,
   *    which is exactly the shape a previously-entered legacy record would have.
   * Neither is a hard gate — this is a "did you already do this?" warning.
   */
  async function runDupeCheck(): Promise<Array<{ id: string; label: string; sub: string }>> {
    const street = form.property_street.trim();
    const city = form.property_city.trim();
    const found = new Map<string, { id: string; label: string; sub: string }>();

    const hints = await findDealOwnershipHints({
      street,
      city,
      phone: form.homeowner_phone.trim() || undefined,
    });
    for (const h of hints) {
      found.set(h.id, {
        id: h.id,
        label: h.homeowner_name ?? "(no name)",
        sub: [h.property_street, h.stage ? `stage ${h.stage}` : null, h.owner_name ? `RM ${h.owner_name}` : null]
          .filter(Boolean)
          .join(" · "),
      });
    }

    const streetNum = street.split(/\s+/)[0] ?? "";
    if (streetNum) {
      try {
        const { data } = await supabase
          .from("deals")
          .select("id, homeowner_name, property_street, property_city, stage")
          .ilike("property_street", `${streetNum}%`)
          .limit(25);
        for (const d of (data ?? []) as Array<{
          id: string; homeowner_name: string | null; property_street: string | null;
          property_city: string | null; stage: string | null;
        }>) {
          const sameCity = !city || (d.property_city ?? "").toLowerCase() === city.toLowerCase();
          if (!sameCity || found.has(d.id)) continue;
          found.set(d.id, {
            id: d.id,
            label: d.homeowner_name ?? "(no name)",
            sub: [d.property_street, d.stage ? `stage ${d.stage}` : null].filter(Boolean).join(" · "),
          });
        }
      } catch {
        /* best effort only */
      }
    }
    return Array.from(found.values());
  }

  async function handleSubmit() {
    setErr(null);

    // Pass 1: duplicate warning. Second press goes through.
    if (!dupeChecked) {
      setChecking(true);
      try {
        const hits = await runDupeCheck();
        setDupeChecked(true);
        if (hits.length > 0) {
          setDupes(hits);
          return;
        }
      } finally {
        setChecking(false);
      }
    }

    setSaving(true);
    try {
      // Server-side column allowlist — see the header. Everything this record
      // must NOT carry (money, commission dates, install_agreement_signed,
      // ho_preferred_build_date, feedback_rating, knock_pin_id) simply has no
      // parameter to arrive through.
      const { data, error } = await supabase.rpc("create_legacy_warranty_deal", {
        p_homeowner_name: form.homeowner_name.trim(),
        p_property_street: form.property_street.trim(),
        p_property_city: form.property_city.trim(),
        p_property_state: (form.property_state.trim() || "GA").toUpperCase(),
        p_property_zip: form.property_zip.trim() || null,
        p_homeowner_phone: form.homeowner_phone.trim() || null,
        p_homeowner_email: form.homeowner_email.trim() || null,
        p_build_completed_date: form.build_completed_date.trim() || null,
        p_warranty_type: form.warranty_type || null,
        p_oc_warranty_number: form.oc_warranty_number.trim() || null,
        p_warranty_registered_date: form.warranty_registered_date.trim() || null,
        p_warranty_expiration_date: form.warranty_expiration_date.trim() || null,
        p_owner_id: form.owner_id || null,
        p_notes: form.notes.trim() || null,
      });
      // supabase.rpc RESOLVES with { error } — it does not reject. An unchecked
      // call here would look like success and navigate to /deal/null.
      if (error) throw error;
      const dealId = data as string;
      if (!dealId) throw new Error("The record was not created — no id came back.");

      await onCreated(dealId);
      navigate(`/deal/${dealId}?tab=warranty`, {
        state: { from: window.location.pathname + window.location.search },
      });
    } catch (e) {
      setErr(fmtSupabaseError(e));
      setSaving(false);
    }
  }

  const buildDate = form.build_completed_date.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-semibold text-bardin-ink">Add legacy job</h2>
            <p className="text-xs text-slate-500">A roof we replaced before the CRM, so a warranty can hang off it</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-lg leading-none">✕</button>
        </div>

        <div className="px-4 py-4 space-y-4">
          {/* Homeowner + property */}
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Homeowner & property</h3>
            <Field label="Homeowner name *" value={form.homeowner_name} onChange={(v) => set("homeowner_name", v)} placeholder="e.g. Schenkman" />
            <div className="grid grid-cols-2 gap-2">
              <Field label="Phone" value={form.homeowner_phone} onChange={(v) => set("homeowner_phone", v)} placeholder="(770) 555-0134" type="tel" />
              <Field label="Email" value={form.homeowner_email} onChange={(v) => set("homeowner_email", v)} type="email" />
            </div>
            <Field label="Street *" value={form.property_street} onChange={(v) => set("property_street", v)} placeholder="3262 Twisted Branches Ln NE" />
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Field label="City *" value={form.property_city} onChange={(v) => set("property_city", v)} placeholder="Marietta" />
              </div>
              <Field label="State" value={form.property_state} onChange={(v) => set("property_state", v)} maxLength={2} />
            </div>
            <Field label="Zip" value={form.property_zip} onChange={(v) => set("property_zip", v)} placeholder="30068" />
          </section>

          {/* The build */}
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">The original build</h3>
            <Field
              label="Install / completion date"
              value={form.build_completed_date}
              onChange={(v) => set("build_completed_date", v)}
              type="date"
              hint="Approximate is fine — this is what dates the warranty term. Leave blank if you truly don't know."
            />
            <div>
              <label className="text-xs font-medium text-slate-700">Sold by (optional)</label>
              <select
                value={form.owner_id}
                onChange={(e) => set("owner_id", e.target.value)}
                className="mt-1 w-full px-2.5 py-1.5 text-sm rounded border border-slate-300 focus:border-bardin-teal focus:outline-none"
              >
                <option value="">— nobody / no longer here —</option>
                {roster.map((r) => <option key={r.id} value={r.id}>{r.full_name}</option>)}
              </select>
              <p className="mt-1 text-[11px] text-slate-500">
                Attribution only. No commission, no leaderboard credit — the record carries no money.
              </p>
            </div>
          </section>

          {/* Warranty */}
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Warranty on file</h3>
            <div>
              <label className="text-xs font-medium text-slate-700">Type</label>
              <select
                value={form.warranty_type}
                onChange={(e) => set("warranty_type", e.target.value)}
                className="mt-1 w-full px-2.5 py-1.5 text-sm rounded border border-slate-300 focus:border-bardin-teal focus:outline-none"
              >
                {WARRANTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <Field label="OC warranty / certificate #" value={form.oc_warranty_number} onChange={(v) => set("oc_warranty_number", v)} />
            <div className="grid grid-cols-2 gap-2">
              <Field label="Registered on" value={form.warranty_registered_date} onChange={(v) => set("warranty_registered_date", v)} type="date" />
              <Field label="Expires" value={form.warranty_expiration_date} onChange={(v) => set("warranty_expiration_date", v)} type="date" />
            </div>
            {buildDate && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] text-slate-500">Set expiry from install:</span>
                {[10, 25, 50].map((yrs) => (
                  <button
                    key={yrs}
                    type="button"
                    onClick={() => set("warranty_expiration_date", addYearsISO(buildDate, yrs))}
                    className="px-2 py-0.5 text-[11px] rounded border border-slate-300 text-slate-600 hover:bg-slate-100"
                  >
                    +{yrs} yr
                  </button>
                ))}
              </div>
            )}
            <p className="text-[11px] text-slate-500">
              Leave &ldquo;Registered on&rdquo; blank if it was never registered with OC — the record will sit in the
              registration SLA panel until it is.
            </p>
          </section>

          <div>
            <label className="text-xs font-medium text-slate-700">Notes</label>
            <textarea
              className="mt-1 w-full px-2.5 py-1.5 text-sm rounded border border-slate-300 focus:border-bardin-teal focus:outline-none min-h-[64px]"
              placeholder="What you know about the original job — year, crew, anything the homeowner told you."
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              maxLength={2000}
            />
          </div>

          {/* Duplicate warning */}
          {dupes && dupes.length > 0 && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded px-2.5 py-2 space-y-1.5">
              <div className="font-semibold text-amber-800">
                {dupes.length} deal{dupes.length === 1 ? "" : "s"} already at this address
              </div>
              <ul className="space-y-1">
                {dupes.slice(0, 5).map((d) => (
                  <li key={d.id} className="text-amber-900">
                    <span className="font-medium">{d.label}</span>
                    {d.sub ? <span className="text-amber-700"> — {d.sub}</span> : null}
                  </li>
                ))}
              </ul>
              <div className="text-amber-800">
                If one of these is the same house, back out and add the warranty to that deal instead.
                Press Create again to add this one anyway.
              </div>
            </div>
          )}

          {err && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5">{err}</div>}

          <div className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded px-2.5 py-2 space-y-1">
            <div className="font-semibold text-slate-700">What this creates</div>
            <div>
              A closed-won record with the contact, the address and the warranty — and nothing else. No scope,
              no invoice, no commission, no crew or leaderboard credit, no PM fee, and no text to the homeowner.
              It&apos;s tagged as an imported legacy record.
            </div>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} disabled={saving} className="px-3 py-1.5 text-xs font-medium rounded bg-slate-200 text-slate-700 hover:bg-slate-300 disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || checking}
            className="px-3 py-1.5 text-xs font-medium rounded bg-bardin-teal text-white hover:bg-bardin-teal/90 disabled:opacity-50"
          >
            {saving ? "Creating…" : checking ? "Checking…" : dupes && dupes.length > 0 ? "Create anyway" : "Create & add warranty"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label, value, onChange, placeholder, type = "text", maxLength, hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  maxLength?: number;
  hint?: string;
}) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-700">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="mt-1 w-full px-2.5 py-1.5 text-sm rounded border border-slate-300 focus:border-bardin-teal focus:outline-none"
      />
      {hint && <p className="mt-1 text-[11px] text-slate-500">{hint}</p>}
    </div>
  );
}
